// TODO: this file is getting pretty long and should probably be split up
// Need to figure out how to do this best
import api from 'services/api';
import {
  AccessTokenDto,
  AuthenticationApi,
  AuthResponse,
  CredentialDto,
  ForgotPasswordRequest,
  LoginWithSsoRequest,
  ProfileApi,
  RegisterWithSsoRequestDto,
  ResetPasswordRequest,
  TwoFactorLoginRequest,
} from 'services/generated';
import { BackendErrorType, handleErrors } from 'services/helpers';
import { rootApi } from 'store/root-api';
import { SSOAuthProviderKey } from 'types/sso';
import { EVENTS, identify, trackEvent } from 'utils/analytics';
import appUrls from 'utils/app-urls';
import { firebaseAuthenticate } from 'utils/firebase';
import { getBrowserLanguage } from 'utils/language';
import { saveTokenToStorage, setToken } from 'utils/token';
import { loginZendesk } from 'utils/zendesk';

const API_URL = appUrls.getEndpointUrl();

const authClient = new AuthenticationApi(
  undefined,
  new URL(API_URL, process.env.REACT_APP_API_URL).href,
  api,
);

const profileClient = new ProfileApi(
  undefined,
  new URL(API_URL, process.env.REACT_APP_API_URL).href,
  api,
);

function registerWithSso(request: RegisterWithSsoRequestDto) {
  return handleErrors(() => profileClient.registerWithProvider(request));
}

function loginWithSso(request: LoginWithSsoRequest) {
  return handleErrors(() => authClient.loginWithProvider(request));
}

export const authApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    loginWithEmailAndPassword: builder.mutation<
      AuthResponse,
      { email: string; password: string; recaptchaToken: string }
    >({
      queryFn: (request, { dispatch }) =>
        handleErrors(async () => {
          const res = await authClient.login({
            username: request.email,
            password: request.password,
            recaptcha: request.recaptchaToken,
          });

          if (res.data) await finalizeLoginOrSignup('login', res.data);

          return res;
        }),
      invalidatesTags: [{ type: 'UserProfile' }],
    }),
    loginTwoFactor: builder.mutation<
      TwoFactorLoginRequest,
      { pin: string; token: string }
    >({
      queryFn: (request, { dispatch }) =>
        handleErrors(async () => {
          const res = await authClient.twoFactorLogin(request);
          await finalizeLoginOrSignup('login', res.data);

          return res;
        }),
    }),
    refreshToken: builder.mutation<AuthResponse, void>({
      queryFn: (_, { dispatch }) =>
        handleErrors(async () => {
          const res = await authClient.refreshToken();
          const token = res.data.token!;
          setToken(token);
          saveTokenToStorage(token);

          return res;
        }),
    }),
    loginOrSignupWithSso: builder.mutation<AuthResponse, SSOAuthProviderKey>({
      queryFn: request =>
        handleErrors(async () => {
          const res = await authenticateWithProvider(request);
          return res;
        }),
      invalidatesTags: [{ type: 'UserProfile' }],
    }),
    signupWithEmailAndPassword: builder.mutation<
      AuthResponse,
      {
        email: string;
        password: string;
        recaptchaToken: string;
        promoCode: string;
      }
    >({
      queryFn: (request, { dispatch }) =>
        handleErrors(async () => {
          const res = await profileClient.register({
            email: request.email,
            password: request.password,
            promoCode: request.promoCode,
            recaptcha: request.recaptchaToken,
            languageCode: getBrowserLanguage(),
          });

          await finalizeLoginOrSignup('signup', res.data);
          return res;
        }),
      invalidatesTags: [{ type: 'UserProfile' }],
    }),
    resendRegistrationEmail: builder.mutation<any, { email: string }>({
      queryFn: request =>
        handleErrors(() =>
          profileClient.resendRegistrationEmail(request.email),
        ),
    }),
    linkSsoProvider: builder.mutation<AuthResponse, SSOAuthProviderKey>({
      queryFn: provider =>
        handleErrors(async () => {
          const { token } = await firebaseAuthenticate(provider);
          return await profileClient.linkWithProvider({
            clientApplicationSsoCode: `DASHBOARD_${provider}`,
            ssoJwt: token!,
          });
        }),
      invalidatesTags: ['LoginMethods'],
    }),
    resetPassword: builder.mutation<AccessTokenDto, ResetPasswordRequest>({
      queryFn: request =>
        handleErrors(() => profileClient.resetPassword(request)),
    }),
    initiatePasswordReset: builder.mutation<void, ForgotPasswordRequest>({
      queryFn: request =>
        handleErrors(() => profileClient.forgotPassword(request)),
    }),
    resetTwoFactor: builder.mutation<void, ResetTwoFactorRequest>({
      queryFn: request =>
        handleErrors(() =>
          authClient.twoFactorReset(
            request.firstName,
            request.lastName,
            request.dateOfBirth,
            request.phoneNumber,
            request.firstDepositDate,
            request.balance,
            request.idDocument,
            request.selfieDocument,
          ),
        ),
    }),
    validateEmail: builder.mutation<AuthResponse, CredentialDto>({
      queryFn: request =>
        handleErrors(async () => {
          const res = await profileClient.validateEmailToken(request);
          if (res.data?.token) {
            setToken(res.data?.token);
            saveTokenToStorage(res.data?.token);
          }
          return res;
        }),
      invalidatesTags: [{ type: 'UserProfile' }],
    }),
  }),
});

async function finalizeLoginOrSignup(
  type: 'signup' | 'login',
  authRes: AuthResponse,
) {
  setToken(authRes.token!);
  saveTokenToStorage(authRes.token!);

  // We want the user to be able to chat using their userId before being fully authenticated
  if (authRes.zendeskToken) {
    loginZendesk(authRes.zendeskToken);
  }

  if (!authRes.twoFactorRequired) {
    //dispatch(setFullyAuthenticatedToken(token));

    const eventType =
      type == 'login' ? EVENTS.LOGIN : EVENTS.REGISTRATION_COMPLETE;
    if (authRes.userId) {
      trackEvent(eventType, { userId: authRes?.userId, email: authRes?.email });
    }
  } else {
    //dispatch(setTokenRequiring2FA(token));
  }

  identify(authRes.userId!);
}

async function authenticateWithProvider(
  provider: SSOAuthProviderKey,
): Promise<{ data: AuthResponse } | { error: BackendErrorType }> {
  const firebaseRes = await firebaseAuthenticate(provider);

  // Login with SSO, needs to be renamed in the backend
  const ssoLoginRes = await loginWithSso({
    ssoJwt: firebaseRes.token!,
    clientApplicationSsoCode: `DASHBOARD_${provider}`,
  });

  if (ssoLoginRes.data) {
    await finalizeLoginOrSignup('login', ssoLoginRes.data);

    return { data: ssoLoginRes.data };
  }

  // If user doesn't exist, register instead
  if (ssoLoginRes.error.code === 'USER_DOES_NOT_EXIST') {
    const signupWithSsoRes = await registerWithSso({
      ssoJwt: firebaseRes.token!,
      clientApplicationSsoCode: `DASHBOARD_${provider}`,
      languageCode: getBrowserLanguage(),
    });

    if (!signupWithSsoRes.error) {
      await finalizeLoginOrSignup('signup', signupWithSsoRes.data);
      return { data: signupWithSsoRes.data };
    } else {
      return { error: signupWithSsoRes.error };
    }
  }

  return { error: ssoLoginRes.error };
}

export const {
  useLoginWithEmailAndPasswordMutation,
  useLoginOrSignupWithSsoMutation,
  useRefreshTokenMutation,
  useSignupWithEmailAndPasswordMutation,
  useLoginTwoFactorMutation,
  useLinkSsoProviderMutation,
  useInitiatePasswordResetMutation,
  useResendRegistrationEmailMutation,
  useResetPasswordMutation,
  useValidateEmailMutation,
  useResetTwoFactorMutation,
} = authApi;
